import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { GraduationCap, Video, Users, Star, Coins, Sparkles, BookOpen, Search, Lightbulb } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useApp } from "@/context/AppContext";

const Center = ({ children }) => <div className="max-w-md mx-auto px-5 pt-32 text-center text-sec">{children}</div>;

export default function TeacherStudio() {
  const { user } = useApp();
  const [me, setMe] = useState(null);
  const [bio, setBio] = useState("");
  const [subjects, setSubjects] = useState("");
  const [insights, setInsights] = useState(null);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/lms/teacher/me").then(({ data }) => setMe(data)).catch(() => {});
  useEffect(() => { if (user) load(); }, [user]);

  const join = async () => {
    setBusy(true);
    try { await api.post("/lms/teacher/join", { bio, subjects: subjects.split(",").map((s) => s.trim()).filter(Boolean) }); toast.success("Welcome to VishwaGuru-University, teacher!"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const goLive = async (cid, title) => {
    try { const { data } = await api.post(`/lms/courses/${cid}/live`, { topic: title }); window.open(data.live.join_url, "_blank"); toast.success("Live room opened — share the link!"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const adopt = async (cid) => {
    try { const { data } = await api.post(`/lms/courses/${cid}/teach`); toast.success(data.message); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const search = async () => { try { const { data } = await api.get(`/lms/catalog?q=${encodeURIComponent(q)}&category=Competitive%20Exams`); setResults((data.courses || []).slice(0, 12)); } catch { /* ignore */ } };
  const getInsights = async () => { try { const { data } = await api.get("/lms/teacher/me/insights"); setInsights(data.insights); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };

  if (user === null) return <Center>Sign in to open Teacher Studio.</Center>;
  if (!me) return <Center>Loading…</Center>;

  if (!me.is_teacher) {
    return (
      <div className="max-w-xl mx-auto px-5 pt-24 pb-16" data-testid="teacher-join">
        <GraduationCap className="text-brand mb-4" size={34} />
        <h1 className="font-serif text-4xl tracking-tight">Become a VishwaGuru-University teacher</h1>
        <p className="text-sec text-sm mt-3">Teach existing free courses or create your own and run live lectures. You cannot set fees — you receive <b className="text-brand">30% of every Guru Dakshina</b> your learners choose to offer.</p>
        <div className="rud-card rounded-2xl p-6 mt-6 space-y-3">
          <textarea data-testid="teacher-bio" value={bio} onChange={(e) => setBio(e.target.value)} rows={3} placeholder="Short bio — your experience & what you love to teach" className="rud-input w-full rounded-lg px-3 py-2 text-sm" />
          <input data-testid="teacher-subjects" value={subjects} onChange={(e) => setSubjects(e.target.value)} placeholder="Subjects (comma-separated) e.g. Physics, Mathematics" className="rud-input w-full rounded-lg px-3 py-2 text-sm" />
          <button data-testid="teacher-join-btn" onClick={join} disabled={busy} className="btn-primary h-11 px-6 rounded-lg text-sm flex items-center gap-2 disabled:opacity-60"><GraduationCap size={16} /> Join as a teacher</button>
        </div>
      </div>
    );
  }

  const t = me.teacher;
  return (
    <div className="max-w-6xl mx-auto px-5 pt-24 pb-16" data-testid="teacher-studio">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-serif text-4xl tracking-tight flex items-center gap-2"><GraduationCap className="text-brand" /> Teacher Studio</h1>
        <a href="/learn" className="btn-ghost rounded-full h-10 px-4 text-sm flex items-center gap-1.5"><BookOpen size={15} /> Create a course</a>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
        {[{ i: Star, l: "Rating", v: `${t.rating_avg || 0}★`, s: `${t.rating_count} reviews` },
          { i: Users, l: "Students", v: t.students, s: `${t.courses_count} courses` },
          { i: Coins, l: "Dakshina earned", v: me.dakshina_received, s: "credits (30% share)" },
          { i: Video, l: "Live now", v: (me.live || []).length, s: "active rooms" }].map((c, i) => (
          <div key={i} className="rud-card rounded-2xl p-5"><c.i size={18} className="text-brand" /><div className="font-serif text-3xl mt-2">{c.v}</div><div className="text-xs text-sec">{c.l} · {c.s}</div></div>
        ))}
      </div>

      {/* My courses */}
      <h2 className="font-serif text-2xl mt-10 mb-3">Your courses</h2>
      {t.courses.length === 0 ? <p className="text-sec text-sm">You aren't teaching any course yet — adopt one below or create your own.</p> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="teacher-courses">
          {t.courses.map((c) => (
            <div key={c.id} className="rud-card rounded-2xl p-5 flex flex-col">
              <div className="text-xs text-brand uppercase tracking-widest">{c.exam_tag || c.category}</div>
              <div className="font-serif text-lg mt-1 leading-tight">{c.title}</div>
              <button data-testid={`golive-${c.id}`} onClick={() => goLive(c.id, c.title)} className="btn-primary rounded-full h-9 px-4 text-sm mt-auto flex items-center gap-1.5 w-max"><Video size={14} /> Go live</button>
            </div>
          ))}
        </div>
      )}

      {/* Adopt a course */}
      <h2 className="font-serif text-2xl mt-10 mb-3">Teach an existing course</h2>
      <div className="flex gap-2 max-w-lg">
        <input data-testid="adopt-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} placeholder="Search competitive-exam courses (e.g. JEE Physics)" className="rud-input flex-1 rounded-lg px-3 py-2 text-sm" />
        <button data-testid="adopt-search-btn" onClick={search} className="btn-primary px-4 rounded-lg text-sm flex items-center gap-1.5"><Search size={15} /></button>
      </div>
      {results.length > 0 && (
        <div className="grid sm:grid-cols-2 gap-3 mt-3" data-testid="adopt-results">
          {results.map((c) => (
            <div key={c.id} className="rud-card rounded-xl p-4 flex items-center justify-between gap-3">
              <div className="min-w-0"><div className="text-sm truncate">{c.title}</div><div className="text-[11px] text-sec">{c.category}</div></div>
              <button data-testid={`adopt-${c.id}`} onClick={() => adopt(c.id)} className="btn-ghost rounded-full h-8 px-3 text-xs shrink-0">Teach this</button>
            </div>
          ))}
        </div>
      )}

      {/* AI improvement insights */}
      <h2 className="font-serif text-2xl mt-10 mb-3 flex items-center gap-2"><Lightbulb className="text-brand" size={20} /> Scope for improvement</h2>
      <div className="rud-card rounded-2xl p-6">
        {!insights ? (
          <button data-testid="insights-btn" onClick={getInsights} className="btn-primary rounded-full h-10 px-5 text-sm flex items-center gap-2"><Sparkles size={15} /> Generate AI recommendations from my reviews</button>
        ) : (
          <div data-testid="teacher-insights" className="space-y-3 text-sm">
            <p className="text-sec">{insights.summary}</p>
            {insights.strengths?.length > 0 && <div><b className="text-brand">Strengths:</b><ul className="list-disc ml-5 text-sec">{insights.strengths.map((s, i) => <li key={i}>{s}</li>)}</ul></div>}
            {insights.improvements?.length > 0 && <div><b className="text-brand">Improve:</b><ul className="list-disc ml-5 text-sec">{insights.improvements.map((s, i) => <li key={i}>{s}</li>)}</ul></div>}
            {insights.actions?.length > 0 && <div><b className="text-brand">Next actions:</b><ul className="list-disc ml-5 text-sec">{insights.actions.map((s, i) => <li key={i}>{s}</li>)}</ul></div>}
          </div>
        )}
      </div>
    </div>
  );
}
